import { useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Upload } from 'lucide-react'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  formationId: string
}

// Records that the state accepted the filing and issued a certificate —
// the tracker's emotional-peak milestone. Certificate number and file
// upload happen together, in one action. Uses a hand-built FormData (like
// DocumentUploader) rather than useForm: Inertia only auto-wraps params
// under the resource key for JSON requests, and a file upload has to be
// multipart, so the acceptance[...] nesting the controller expects has to
// be built by hand here.
export function CertificateDialog({ open, onOpenChange, formationId }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [certificateNumber, setCertificateNumber] = useState('')
  const [certificateFile, setCertificateFile] = useState<File | null>(null)
  const [processing, setProcessing] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  function resetForm() {
    setCertificateNumber('')
    setCertificateFile(null)
    setErrors({})
    if (fileRef.current) fileRef.current.value = ''
  }

  function handleClose(success: boolean) {
    onOpenChange(false)
    if (!success) resetForm()
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const formData = new FormData()
    formData.append('acceptance[certificate_number]', certificateNumber)
    if (certificateFile) formData.append('acceptance[certificate_file]', certificateFile)

    setProcessing(true)
    router.post(`/backoffice/formations/${formationId}/state_filing/acceptance`, formData, {
      forceFormData: true,
      onSuccess: () => {
        resetForm()
        handleClose(true)
      },
      onError: (formErrors) => setErrors(formErrors as unknown as Record<string, string>),
      onFinish: () => setProcessing(false)
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-md overflow-y-auto">
        <DialogHeader className="mb-6">
          <DialogTitle>Record certificate</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 px-4">
          <div className="space-y-2">
            <Label htmlFor="certificate_number">
              Certificate number <span className="text-destructive">*</span>
            </Label>
            <Input
              id="certificate_number"
              placeholder="e.g. WY-CERT-00456"
              value={certificateNumber}
              onChange={(e) => setCertificateNumber(e.target.value)}
              required
            />
            {errors.certificate_number && <p className="text-sm text-destructive">{errors.certificate_number}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="certificate_file">Certificate file</Label>
            <div className="flex items-center gap-2">
              <Input
                ref={fileRef}
                id="certificate_file"
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setCertificateFile(e.target.files?.[0] ?? null)}
              />
              <Upload className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
            </div>
            <p className="text-xs text-muted-foreground">Shown to the founder as their certificate download.</p>
            {errors.certificate_file && <p className="text-sm text-destructive">{errors.certificate_file}</p>}
          </div>

          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={processing}>
                {processing ? 'Recording...' : 'Record certificate'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
